// Opciones y pedidos (Fase 1). Staff: define grupos de opción del menú (elige 1 de N),
// ve el resumen de pedidos por día/servicio y publica el kiosco por token.
// Catering → Operación → Opciones y pedidos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { ListChecks, Save, Loader2, Star, QrCode, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { cat, catRpc, invalidateCatering, SERVICIO_LABEL, DIAS } from './shared';

interface Contrato { id: string; nombre: string; ciclo_id: string | null; modo_servicio: 'bloque' | 'comensal' }
interface Opcion { id: string; receta_id: string; receta: string; grupo: string | null; es_default: boolean; orden: number }
interface Resumen { grupo: string; receta_id: string; receta: string; es_default: boolean; elegidos: number }

const SERVICIOS = Object.keys(SERVICIO_LABEL);
const isoHoy = (): string => new Date().toISOString().slice(0, 10);

export function OpcionesCatering() {
  const qc = useQueryClient();
  const [contratoId, setContratoId] = useState('');
  const [dia, setDia] = useState(1);
  const [servicio, setServicio] = useState('almuerzo_1');
  const [fecha, setFecha] = useState(isoHoy());

  const { data: contratos = [] } = useQuery<Contrato[]>({
    queryKey: ['cat-contratos-opciones'],
    queryFn: () => cat.list<Contrato>('contratos', '?select=id,nombre,ciclo_id,modo_servicio&es_activo=is.true&deleted_at=is.null&order=nombre'),
  });
  const contrato = contratos.find((c) => c.id === contratoId) ?? contratos[0];
  const ciclo = contrato?.ciclo_id ?? '';

  const { data: opciones = [], isLoading } = useQuery<Opcion[]>({
    queryKey: ['cat-menu-opciones', ciclo, dia, servicio],
    queryFn: () => catRpc<Opcion[]>('cat_menu_opciones', { _ciclo_id: ciclo, _dia: dia, _servicio: servicio }),
    enabled: !!ciclo,
  });
  const { data: resumen = [] } = useQuery<Resumen[]>({
    queryKey: ['cat-pedidos-resumen', contrato?.id, fecha, servicio],
    queryFn: () => catRpc<Resumen[]>('cat_pedidos_resumen', { _contrato_id: contrato?.id, _fecha: fecha, _servicio: servicio }),
    enabled: !!contrato?.id,
  });
  const { data: kiosco } = useQuery<{ token: string }>({
    queryKey: ['cat-kiosco-token', contrato?.id],
    queryFn: () => catRpc<{ token: string }>('cat_kiosco_token', { _contrato_id: contrato?.id }),
    enabled: !!contrato?.id,
  });

  const setGrupo = useMutation({
    mutationFn: (p: { id: string; grupo: string; es_default: boolean }) => catRpc('cat_menu_grupo_set', { _ciclo_menu_id: p.id, _grupo: p.grupo || null, _es_default: p.es_default }),
    onSuccess: () => { invalidateCatering(qc); toast.success('Menú actualizado'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kioscoUrl = kiosco ? `${window.location.origin}/kiosco/${kiosco.token}` : '';

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><ListChecks className="h-4 w-4" /> Opciones y pedidos</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div>
              <label className="block text-xs text-muted-foreground">Contrato</label>
              <select className={`${fieldCls} w-72`} value={contrato?.id ?? ''} onChange={(e) => setContratoId(e.target.value)}>
                {contratos.map((c) => <option key={c.id} value={c.id}>{c.nombre} · {c.modo_servicio}</option>)}
              </select>
            </div>
          </div>
          {contrato?.modo_servicio !== 'comensal' && (
            <p className="pt-1 text-xs text-amber-600">Este contrato está en modo «bloque»: los grupos de opción sirven solo la opción por defecto × pax. Cámbialo a «comensal» en Comensales para que cada persona elija.</p>
          )}
        </CardHeader>
      </Card>

      {/* Opciones del menú */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Grupos de opción del menú</CardTitle>
          <p className="text-xs text-muted-foreground">Recetas con el mismo <b>grupo</b> son elegibles (el comensal elige 1). Sin grupo = base (siempre se sirve). Marca una como <b>predeterminada</b> (la de quien no elige).</p>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Día</label>
              <select className={`${fieldCls} w-36`} value={dia} onChange={(e) => setDia(Number(e.target.value))}>
                {DIAS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
              </select>
            </div>
            <div><label className="block text-xs text-muted-foreground">Servicio</label>
              <select className={`${fieldCls} w-44`} value={servicio} onChange={(e) => setServicio(e.target.value)}>
                {SERVICIOS.map((s) => <option key={s} value={s}>{SERVICIO_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="py-4 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Cargando…</p>}
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Receta</th><th className="py-1 pr-2">Grupo (vacío = base)</th><th className="py-1 pr-2 text-center">Predet.</th><th className="py-1 pr-2"></th></tr></thead>
                <tbody>
                  {opciones.map((o) => <OpcionRow key={o.id} o={o} saving={setGrupo.isPending} onSave={(grupo, def) => setGrupo.mutate({ id: o.id, grupo, es_default: def })} />)}
                  {!opciones.length && <tr><td colSpan={4} className="py-3 text-sm text-muted-foreground">Este día/servicio no tiene menú en el ciclo.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Pedidos del día */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm"><ClipboardList className="h-4 w-4" /> Pedidos del día</CardTitle>
            <div className="flex flex-wrap items-end gap-2 pt-1">
              <div><label className="block text-xs text-muted-foreground">Fecha</label><input type="date" className={`${fieldCls} w-40`} value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
              <div><label className="block text-xs text-muted-foreground">Servicio</label>
                <select className={`${fieldCls} w-40`} value={servicio} onChange={(e) => setServicio(e.target.value)}>
                  {SERVICIOS.map((s) => <option key={s} value={s}>{SERVICIO_LABEL[s]}</option>)}
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {resumen.length ? (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Opción</th><th className="py-1 pr-2 text-right">Eligieron</th></tr></thead>
                <tbody>
                  {resumen.map((x) => (
                    <tr key={x.receta_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-2">{x.receta.replace('Plato fuerte — ', '')} {x.es_default && <Star className="inline h-3 w-3 text-amber-500" />}<span className="block text-[11px] text-muted-foreground">grupo {x.grupo}</span></td>
                      <td className="py-1.5 pr-2 text-right font-mono">{num(x.elegidos, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="py-3 text-sm text-muted-foreground">Sin grupos de opción para esa fecha/servicio (o sin pedidos).</p>}
          </CardContent>
        </Card>

        {/* Kiosco */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><QrCode className="h-4 w-4" /> Kiosco del comensal</CardTitle>
            <p className="text-xs text-muted-foreground">Pantalla pública (sin login) para que el comensal escanee su badge y elija. Colócala en el comedor.</p>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-4">
            {kioscoUrl ? (
              <>
                <div className="rounded-md bg-white p-2"><QRCodeSVG value={kioscoUrl} size={128} /></div>
                <div className="text-xs">
                  <p className="break-all font-mono text-muted-foreground">{kioscoUrl}</p>
                  <a className="mt-1 inline-block text-primary underline" href={kioscoUrl} target="_blank" rel="noreferrer">Abrir kiosco</a>
                </div>
              </>
            ) : <p className="text-sm text-muted-foreground">Selecciona un contrato.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function OpcionRow({ o, onSave, saving }: { o: Opcion; onSave: (grupo: string, def: boolean) => void; saving: boolean }) {
  const [grupo, setGrupo] = useState(o.grupo ?? '');
  const [def, setDef] = useState(o.es_default);
  const changed = grupo !== (o.grupo ?? '') || def !== o.es_default;
  return (
    <tr className="border-b last:border-0">
      <td className="py-1.5 pr-2 font-medium">{o.receta.replace('Plato fuerte — ', '')}</td>
      <td className="py-1.5 pr-2"><input className={`${fieldCls} w-40`} placeholder="(base)" value={grupo} onChange={(e) => setGrupo(e.target.value)} /></td>
      <td className="py-1.5 pr-2 text-center"><input type="checkbox" checked={def} disabled={!grupo} onChange={(e) => setDef(e.target.checked)} /></td>
      <td className="py-1.5 pr-2 text-right">{changed && <Button size="sm" className="h-7 gap-1 px-2 text-xs" disabled={saving} onClick={() => onSave(grupo, def)}><Save className="h-3.5 w-3.5" /> Guardar</Button>}</td>
    </tr>
  );
}
